import { createClient } from '@supabase/supabase-js';
import { initCommandes, setSupabaseClient } from './commandes.js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

let params = {
    taxe_alcool: 29.0,
    taux_change: 0.96,
    tva: 1.081,
    marge_prive: 1.8,
    marge_resto: 1.75,
    marge_pro: 1.5
};

/* =========================
   AUTH + LAYOUT
========================= */
const authScreen = document.getElementById('auth-screen');
const appShell = document.getElementById('app-shell');

const tabSignin = document.getElementById('tab-signin');
const tabSignup = document.getElementById('tab-signup');
const authForm = document.getElementById('auth-form');
const authEmail = document.getElementById('auth-email');
const authPassword = document.getElementById('auth-password');
const authSubmit = document.getElementById('auth-submit');
const authError = document.getElementById('auth-error');

const btnLogout = document.getElementById('btn-logout');
const btnLogoutTop = document.getElementById('btn-logout-top');

let authMode = 'signin'; // signin | signup
let appInitialized = false;

function showAuth(message = '') {
    if (appShell) {
        appShell.style.display = 'none';
        appShell.setAttribute('aria-hidden', 'true');
    }
    if (authScreen) {
        authScreen.style.display = 'grid';
        authScreen.setAttribute('aria-hidden', 'false');
    }

    if (authError) {
        if (message) {
            authError.textContent = message;
            authError.style.display = 'block';
        } else {
            authError.textContent = '';
            authError.style.display = 'none';
        }
    }
}

function showApp() {
    if (authScreen) {
        authScreen.style.display = 'none';
        authScreen.setAttribute('aria-hidden', 'true');
    }
    if (appShell) {
        appShell.style.display = 'flex';
        appShell.setAttribute('aria-hidden', 'false');
    }
    if (authError) {
        authError.textContent = '';
        authError.style.display = 'none';
    }
}

function setAuthMode(mode) {
    authMode = mode;

    const isSignin = mode === 'signin';
    tabSignin?.classList.toggle('is-active', isSignin);
    tabSignup?.classList.toggle('is-active', !isSignin);

    if (authSubmit) authSubmit.textContent = isSignin ? 'Se connecter' : 'Créer un compte';
    if (authPassword) authPassword.autocomplete = isSignin ? 'current-password' : 'new-password';
}

tabSignin?.addEventListener('click', () => setAuthMode('signin'));
tabSignup?.addEventListener('click', () => setAuthMode('signup'));

authForm?.addEventListener('submit', async (e) => {
    e.preventDefault();

    const email = (authEmail?.value || '').trim();
    const password = authPassword?.value || '';

    if (!email || !password) {
        showAuth('Entrez un email et un mot de passe.');
        return;
    }

    try {
        if (authMode === 'signin') {
            const { error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) throw error;
        } else {
            const { error } = await supabase.auth.signUp({ email, password });
            if (error) throw error;
        }
    } catch (err) {
        showAuth(err?.message || 'Erreur de connexion.');
    }
});

async function doLogout() {
    try {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
    } catch (err) {
        showToast('Erreur déconnexion', 'error');
    }
}

btnLogout?.addEventListener('click', doLogout);
btnLogoutTop?.addEventListener('click', doLogout);

async function ensureSignedInOrShowAuth() {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
        showAuth(error.message);
        return null;
    }
    const session = data?.session || null;
    if (!session) {
        showAuth();
        return null;
    }
    showApp();
    return session;
}

// Sur changement de session (logout / login)
supabase.auth.onAuthStateChange(async (_event, session) => {
    if (!session) {
        showAuth();
        appInitialized = false;
        return;
    }

    showApp();
    if (!appInitialized) {
        await init();
    }
});

/* =========================
   SIDEBAR
========================= */
const sidebar = document.getElementById('sidebar');
const overlay = document.getElementById('sidebar-overlay');
const toggleBtn = document.getElementById('sidebar-toggle');
const collapseBtn = document.getElementById('sidebar-collapse');

const LS_KEY = 'sidebar_state'; // expanded | collapsed

function isMobile() {
    return window.matchMedia('(max-width: 900px)').matches;
}

function setCollapsed(isCollapsed) {
    if (!sidebar) return;
    sidebar.classList.toggle('is-collapsed', isCollapsed);
    localStorage.setItem(LS_KEY, isCollapsed ? 'collapsed' : 'expanded');
}

function openMobile() {
    if (!sidebar || !overlay) return;
    sidebar.classList.add('is-open');
    overlay.classList.add('is-show');
    overlay.setAttribute('aria-hidden', 'false');
}

function closeMobile() {
    if (!sidebar || !overlay) return;
    sidebar.classList.remove('is-open');
    overlay.classList.remove('is-show');
    overlay.setAttribute('aria-hidden', 'true');
}

toggleBtn?.addEventListener('click', () => {
    if (isMobile()) openMobile();
    else setCollapsed(!sidebar?.classList.contains('is-collapsed'));
});

collapseBtn?.addEventListener('click', () => {
    setCollapsed(!sidebar?.classList.contains('is-collapsed'));
});

overlay?.addEventListener('click', closeMobile);

document.querySelectorAll('.nav-item').forEach((btn) => {
    btn.addEventListener('click', () => {
        const targetId = btn.getAttribute('data-target');
        const el = document.getElementById(targetId);
        if (el) {
            if (el.tagName?.toLowerCase() === 'details') el.open = true;
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (isMobile()) closeMobile();
    });
});

(function initSidebarState() {
    const state = localStorage.getItem(LS_KEY);
    if (state === 'collapsed') setCollapsed(true);
})();

/* =========================
   APP INIT
========================= */
async function init() {
    const session = await ensureSignedInOrShowAuth();
    if (!session) return;

    if (appInitialized) return;
    appInitialized = true;

    setSupabaseClient(supabase);

    await loadParams();
    await loadSuppliers();
    await loadProducts();
    await loadStockHistory();
    updateStockProductSelect();
    await initCommandes();
}

/* =========================
   DATA LOADERS
========================= */
async function loadParams() {
    try {
        const { data } = await supabase
            .from('parametres')
            .select('*')
            .eq('id', 1)
            .maybeSingle();

        if (data) {
            params = {
                taxe_alcool: parseFloat(data.taxe_alcool),
                taux_change: parseFloat(data.taux_change),
                tva: parseFloat(data.tva),
                marge_prive: parseFloat(data.marge_prive),
                marge_resto: parseFloat(data.marge_resto),
                marge_pro: parseFloat(data.marge_pro)
            };

            document.getElementById('taxe_alcool').value = params.taxe_alcool;
            document.getElementById('taux_change').value = params.taux_change;
            document.getElementById('tva').value = params.tva;
            document.getElementById('marge_prive').value = params.marge_prive;
            document.getElementById('marge_resto').value = params.marge_resto;
            document.getElementById('marge_pro').value = params.marge_pro;
        }
    } catch (err) {
        console.error('Erreur chargement parametres:', err);
    }
}

async function loadSuppliers() {
    try {
        const { data: suppliers, error } = await supabase
            .from('fournisseurs')
            .select('*')
            .order('nom', { ascending: true });

        if (error) throw error;

        const fournisseurSelect = document.getElementById('fournisseur');
        const editFournisseurSelect = document.getElementById('edit-fournisseur');
        const filterSupplierSelect = document.getElementById('filter-supplier');

        fournisseurSelect.innerHTML = '<option value="">Sélectionner un fournisseur...</option>';
        editFournisseurSelect.innerHTML = '<option value="">Sélectionner un fournisseur...</option>';
        filterSupplierSelect.innerHTML = '<option value="">Tous les fournisseurs</option>';

        if (suppliers) {
            suppliers.forEach(supplier => {
                const option1 = document.createElement('option');
                option1.value = supplier.nom;
                option1.textContent = supplier.nom;
                fournisseurSelect.appendChild(option1);

                const option2 = document.createElement('option');
                option2.value = supplier.nom;
                option2.textContent = supplier.nom;
                editFournisseurSelect.appendChild(option2);

                const option3 = document.createElement('option');
                option3.value = supplier.nom;
                option3.textContent = supplier.nom;
                filterSupplierSelect.appendChild(option3);
            });
        }

        displaySuppliersList(suppliers || []);
    } catch (err) {
        console.error('Erreur chargement fournisseurs:', err);
    }
}

function displaySuppliersList(suppliers) {
    const container = document.getElementById('suppliers-list-container');
    container.innerHTML = '';

    if (suppliers.length === 0) {
        container.innerHTML = '<p style="color: var(--text-light);">Aucun fournisseur</p>';
        return;
    }

    suppliers.forEach(supplier => {
        const item = document.createElement('div');
        item.className = 'supplier-item';
        item.innerHTML = `
            <span class="supplier-name">${supplier.nom}</span>
            <button class="btn btn-danger" onclick="deleteSupplier('${supplier.id}', '${supplier.nom}')">🗑️ Supprimer</button>
        `;
        container.appendChild(item);
    });
}

function calculatePrices(pa_eur, volume_l, alcool_pct) {
    const taxe_alcool_chf = volume_l * (alcool_pct / 100) * params.taxe_alcool;
    const base_chf = pa_eur * params.taux_change;
    const cout_avant_tva = base_chf + taxe_alcool_chf;
    const pa_chf_taxe = cout_avant_tva * params.tva;
    const prix_prive = pa_chf_taxe * params.marge_prive;
    const prix_resto = pa_chf_taxe * params.marge_resto;
    const prix_pro = pa_chf_taxe * params.marge_pro;

    return {
        taxe_alcool_chf: parseFloat(taxe_alcool_chf.toFixed(2)),
        pa_chf_taxe: parseFloat(pa_chf_taxe.toFixed(2)),
        prix_prive: parseFloat(prix_prive.toFixed(2)),
        prix_resto: parseFloat(prix_resto.toFixed(2)),
        prix_pro: parseFloat(prix_pro.toFixed(2))
    };
}

async function loadProducts() {
    try {
        const { data: products, error } = await supabase
            .from('produits')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;

        displayProducts(products || []);
    } catch (err) {
        console.error('Erreur chargement produits:', err);
        showToast('Erreur chargement des produits', 'error');
    }
}

function displayProducts(products) {
    const tbody = document.getElementById('products-tbody');
    tbody.innerHTML = '';

    if (!products || products.length === 0) {
        tbody.innerHTML = '<tr><td colspan="13" style="text-align: center;">Aucun produit</td></tr>';
        updateStats([]);
        return;
    }

    products.forEach(product => {
        const row = document.createElement('tr');
        row.innerHTML = `
            <td>${product.fournisseur}</td>
            <td>${product.nom_produit}</td>
            <td>${product.volume_l}</td>
            <td>${product.alcool_pct}%</td>
            <td>${product.carton}</td>
            <td>${parseFloat(product.pa_eur).toFixed(2)}</td>
            <td>${parseFloat(product.taxe_alcool_chf).toFixed(2)}</td>
            <td>${parseFloat(product.pa_chf_taxe).toFixed(2)}</td>
            <td>${parseFloat(product.prix_prive).toFixed(2)}</td>
            <td>${parseFloat(product.prix_resto).toFixed(2)}</td>
            <td>${parseFloat(product.prix_pro).toFixed(2)}</td>
            <td>${product.stock}</td>
            <td>
                <button class="btn-edit" onclick="editProduct('${product.id}')">✏️</button>
                <button class="btn-delete" onclick="deleteProduct('${product.id}')">🗑️</button>
            </td>
        `;
        tbody.appendChild(row);
    });

    updateStats(products);
}

function updateStats(products) {
    const totalProducts = products.length;
    const suppliers = [...new Set(products.map(p => p.fournisseur))];
    const totalStock = products.reduce((sum, p) => sum + p.stock, 0);
    const totalValue = products.reduce((sum, p) => sum + (p.pa_chf_taxe * p.stock), 0);

    document.getElementById('stat-products').textContent = totalProducts;
    document.getElementById('stat-suppliers').textContent = suppliers.length;
    document.getElementById('stat-stock').textContent = totalStock;
    document.getElementById('stat-value').textContent = `${totalValue.toFixed(2)} CHF`;

    const supplierStats = {};
    products.forEach(p => {
        if (!supplierStats[p.fournisseur]) {
            supplierStats[p.fournisseur] = { count: 0, stock: 0, value: 0 };
        }
        supplierStats[p.fournisseur].count++;
        supplierStats[p.fournisseur].stock += p.stock;
        supplierStats[p.fournisseur].value += p.pa_chf_taxe * p.stock;
    });

    const suppliersTbody = document.getElementById('suppliers-tbody');
    suppliersTbody.innerHTML = '';

    Object.entries(supplierStats).forEach(([supplier, stats]) => {
        const row = document.createElement('tr');
        row.innerHTML = `
            <td>${supplier}</td>
            <td>${stats.count}</td>
            <td>${stats.stock}</td>
            <td>${stats.value.toFixed(2)}</td>
        `;
        suppliersTbody.appendChild(row);
    });
}

async function loadStockHistory() {
    try {
        const { data: history, error } = await supabase
            .from('mouvements_stock')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(50);

        if (error) throw error;

        const tbody = document.getElementById('history-tbody');
        tbody.innerHTML = '';

        if (!history || history.length === 0) {
            tbody.innerHTML = '<tr><td colspan="5" style="text-align: center;">Aucun mouvement</td></tr>';
            return;
        }

        history.forEach(item => {
            const row = document.createElement('tr');
            const date = new Date(item.created_at).toLocaleString('fr-FR');
            row.innerHTML = `
                <td>${date}</td>
                <td>${item.nom_produit}</td>
                <td>${item.quantite > 0 ? '+' : ''}${item.quantite}</td>
                <td>${item.type_mouvement}</td>
                <td>${item.stock_apres}</td>
            `;
            tbody.appendChild(row);
        });
    } catch (err) {
        console.error('Erreur chargement historique:', err);
    }
}

function updateStockProductSelect() {
    supabase
        .from('produits')
        .select('id, nom_produit, fournisseur')
        .order('nom_produit', { ascending: true })
        .then(({ data: products }) => {
            const select = document.getElementById('stock-product');
            select.innerHTML = '<option value="">Sélectionner...</option>';

            if (products) {
                products.forEach(p => {
                    const option = document.createElement('option');
                    option.value = p.id;
                    option.textContent = `${p.fournisseur} - ${p.nom_produit}`;
                    select.appendChild(option);
                });
            }
        });
}

function showToast(message, type = 'success') {
    const toast = document.getElementById('toast');
    toast.textContent = message;
    toast.className = `toast ${type} show`;
    setTimeout(() => {
        toast.className = 'toast';
    }, 3000);
}

/* =========================
   EVENTS
========================= */
document.getElementById('add-product-form').addEventListener('submit', async (e) => {
    e.preventDefault();

    const fournisseur = document.getElementById('fournisseur').value;
    const nom_produit = document.getElementById('nom_produit').value;
    const volume_l = parseFloat(document.getElementById('volume_l').value);
    const alcool_pct = parseFloat(document.getElementById('alcool_pct').value);
    const carton = parseInt(document.getElementById('carton').value);
    const pa_eur = parseFloat(document.getElementById('pa_eur').value);
    const stock = parseInt(document.getElementById('stock_initial').value);

    const calculated = calculatePrices(pa_eur, volume_l, alcool_pct);

    try {
        const { error } = await supabase.from('produits').insert([{
            fournisseur,
            nom_produit,
            volume_l,
            alcool_pct,
            carton,
            pa_eur,
            taxe_alcool_chf: calculated.taxe_alcool_chf,
            pa_chf_taxe: calculated.pa_chf_taxe,
            prix_prive: calculated.prix_prive,
            prix_resto: calculated.prix_resto,
            prix_pro: calculated.prix_pro,
            stock
        }]);

        if (error) throw error;

        showToast('Produit ajouté avec succès');
        e.target.reset();
        await loadProducts();
        updateStockProductSelect();
    } catch (err) {
        console.error('Erreur ajout produit:', err);
        showToast('Erreur lors de l\'ajout', 'error');
    }
});

document.getElementById('save-params').addEventListener('click', async () => {
    const newParams = {
        taxe_alcool: parseFloat(document.getElementById('taxe_alcool').value),
        taux_change: parseFloat(document.getElementById('taux_change').value),
        tva: parseFloat(document.getElementById('tva').value),
        marge_prive: parseFloat(document.getElementById('marge_prive').value),
        marge_resto: parseFloat(document.getElementById('marge_resto').value),
        marge_pro: parseFloat(document.getElementById('marge_pro').value)
    };

    try {
        const { error } = await supabase
            .from('parametres')
            .upsert({ id: 1, ...newParams, updated_at: new Date().toISOString() });

        if (error) throw error;

        params = newParams;
        showToast('Paramètres sauvegardés');
    } catch (err) {
        console.error('Erreur sauvegarde parametres:', err);
        showToast('Erreur sauvegarde parametres', 'error');
    }
});

document.getElementById('stock-form').addEventListener('submit', async (e) => {
    e.preventDefault();

    const productId = document.getElementById('stock-product').value;
    const quantity = parseInt(document.getElementById('stock-quantity').value);

    if (!productId) {
        showToast('Sélectionnez un produit', 'warning');
        return;
    }

    try {
        const { data: product, error: fetchError } = await supabase
            .from('produits')
            .select('*')
            .eq('id', productId)
            .maybeSingle();

        if (fetchError || !product) {
            showToast('Produit introuvable', 'error');
            return;
        }

        const newStock = product.stock + quantity;

        if (newStock < 0) {
            showToast('Stock insuffisant', 'error');
            return;
        }

        const { error: updateError } = await supabase
            .from('produits')
            .update({ stock: newStock, updated_at: new Date().toISOString() })
            .eq('id', productId);

        if (updateError) throw updateError;

        await supabase.from('mouvements_stock').insert([{
            produit_id: productId,
            nom_produit: product.nom_produit,
            quantite: quantity,
            type_mouvement: quantity > 0 ? 'Entrée' : 'Sortie',
            stock_apres: newStock
        }]);

        showToast('Stock mis à jour');
        e.target.reset();
        await loadProducts();
        await loadStockHistory();
    } catch (err) {
        console.error('Erreur mise à jour stock:', err);
        showToast('Erreur mise à jour stock', 'error');
    }
});

async function filterProducts() {
    const searchTerm = document.getElementById('search').value.toLowerCase();
    const selectedSupplier = document.getElementById('filter-supplier').value;

    const { data: products } = await supabase
        .from('produits')
        .select('*')
        .order('created_at', { ascending: false });

    if (products) {
        let filtered = products;

        if (searchTerm) {
            filtered = filtered.filter(p =>
                p.nom_produit.toLowerCase().includes(searchTerm) ||
                p.fournisseur.toLowerCase().includes(searchTerm)
            );
        }

        if (selectedSupplier) {
            filtered = filtered.filter(p => p.fournisseur === selectedSupplier);
        }

        displayProducts(filtered);
    }
}

document.getElementById('search').addEventListener('input', filterProducts);
document.getElementById('filter-supplier').addEventListener('change', filterProducts);

document.getElementById('close-modal').addEventListener('click', () => {
    document.getElementById('edit-modal').style.display = 'none';
});

document.getElementById('cancel-edit').addEventListener('click', () => {
    document.getElementById('edit-modal').style.display = 'none';
});

window.editProduct = async (id) => {
    try {
        const { data: product, error } = await supabase
            .from('produits')
            .select('*')
            .eq('id', id)
            .maybeSingle();

        if (error || !product) {
            showToast('Produit introuvable', 'error');
            return;
        }

        document.getElementById('edit-product-id').value = product.id;
        document.getElementById('edit-fournisseur').value = product.fournisseur;
        document.getElementById('edit-nom-produit').value = product.nom_produit;
        document.getElementById('edit-volume').value = product.volume_l;
        document.getElementById('edit-alcool').value = product.alcool_pct;
        document.getElementById('edit-carton').value = product.carton;
        document.getElementById('edit-pa-eur').value = product.pa_eur;

        document.getElementById('edit-modal').style.display = 'flex';
    } catch (err) {
        console.error('Erreur chargement produit:', err);
        showToast('Erreur chargement produit', 'error');
    }
};

window.deleteProduct = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;

    try {
        const { error } = await supabase
            .from('produits')
            .delete()
            .eq('id', id);

        if (error) throw error;

        showToast('Produit supprimé');
        await loadProducts();
    } catch (err) {
        console.error('Erreur suppression produit:', err);
        showToast('Erreur suppression produit', 'error');
    }
};

document.getElementById('edit-product-form').addEventListener('submit', async (e) => {
    e.preventDefault();

    const id = document.getElementById('edit-product-id').value;
    const fournisseur = document.getElementById('edit-fournisseur').value;
    const nom_produit = document.getElementById('edit-nom-produit').value;
    const volume_l = parseFloat(document.getElementById('edit-volume').value);
    const alcool_pct = parseFloat(document.getElementById('edit-alcool').value);
    const carton = parseInt(document.getElementById('edit-carton').value);
    const pa_eur = parseFloat(document.getElementById('edit-pa-eur').value);

    const calculated = calculatePrices(pa_eur, volume_l, alcool_pct);

    try {
        const { error } = await supabase
            .from('produits')
            .update({
                fournisseur,
                nom_produit,
                volume_l,
                alcool_pct,
                carton,
                pa_eur,
                taxe_alcool_chf: calculated.taxe_alcool_chf,
                pa_chf_taxe: calculated.pa_chf_taxe,
                prix_prive: calculated.prix_prive,
                prix_resto: calculated.prix_resto,
                prix_pro: calculated.prix_pro,
                updated_at: new Date().toISOString()
            })
            .eq('id', id);

        if (error) throw error;

        showToast('Produit mis à jour');
        document.getElementById('edit-modal').style.display = 'none';
        await loadProducts();
    } catch (err) {
        console.error('Erreur mise à jour produit:', err);
        showToast('Erreur mise à jour produit', 'error');
    }
});

document.getElementById('add-supplier-form').addEventListener('submit', async (e) => {
    e.preventDefault();

    const nom = document.getElementById('new-supplier-name').value.trim();

    if (!nom) {
        showToast('Veuillez entrer un nom de fournisseur', 'warning');
        return;
    }

    try {
        const { data: existing } = await supabase
            .from('fournisseurs')
            .select('nom')
            .eq('nom', nom)
            .maybeSingle();

        if (existing) {
            showToast('Ce fournisseur existe déjà', 'warning');
            return;
        }

        const { error } = await supabase
            .from('fournisseurs')
            .insert([{ nom }]);

        if (error) throw error;

        showToast('Fournisseur ajouté avec succès');
        e.target.reset();
        await loadSuppliers();
    } catch (err) {
        console.error('Erreur ajout fournisseur:', err);
        showToast('Erreur lors de l\'ajout du fournisseur', 'error');
    }
});

window.deleteSupplier = async (id, nom) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le fournisseur "${nom}" ?`)) return;

    try {
        const { data: products } = await supabase
            .from('produits')
            .select('id')
            .eq('fournisseur', nom)
            .limit(1);

        if (products && products.length > 0) {
            showToast('Impossible de supprimer : des produits utilisent ce fournisseur', 'error');
            return;
        }

        const { error } = await supabase
            .from('fournisseurs')
            .delete()
            .eq('id', id);

        if (error) throw error;

        showToast('Fournisseur supprimé');
        await loadSuppliers();
    } catch (err) {
        console.error('Erreur suppression fournisseur:', err);
        showToast('Erreur suppression fournisseur', 'error');
    }
};

document.getElementById('toggle-add-product').addEventListener('click', () => {
    const form = document.getElementById('add-product-form');
    const toggleBtn = document.getElementById('toggle-add-product');

    if (form.style.display === 'none') {
        form.style.display = 'block';
        toggleBtn.innerHTML = '<span id="toggle-icon">▼</span> Masquer';
    } else {
        form.style.display = 'none';
        toggleBtn.innerHTML = '<span id="toggle-icon">▶</span> Afficher';
    }
});

/* =========================
   START
========================= */
setAuthMode('signin');
showAuth();
init();
